import React, { useEffect, useMemo, useState } from 'react';
import { Box, createStyles, makeStyles, MenuItem, MenuList, Popover, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import Spinner from '@/se/components/Spinner';

export interface PopupSelectMenuOption<TId = string> {
  id: TId;
  label: string;
}

export interface PopupSelectMenuProps<TId = string> {
  options?: PopupSelectMenuOption<TId>[];
  selectedId?: TId;
  editable?: boolean;
  onSelect: (id: TId) => Promise<void> | void;
  onRemove?: () => Promise<void> | void;
  removeOption?: React.ReactNode;
  renderOption?: (params: { option: PopupSelectMenuOption<TId>; isSelected: boolean }) => React.ReactNode;
  menuTitle?: React.ReactNode;
  showSearch?: boolean;
  menuItemClassName?: string;
  renderTrigger: (params: {
    open: boolean;
    selectedOption?: PopupSelectMenuOption<TId>;
    onClick: (event: React.MouseEvent<any>) => void;
  }) => React.ReactNode;
}

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      position: 'relative',
      maxHeight: '38rem',
      overflow: 'hidden',
    },
    content: {
      position: 'relative',
    },
    searchRow: {
      display: 'flex',
      alignItems: 'center',
    },
    menu: {
      maxHeight: '28rem',
      overflowY: 'scroll',
      paddingTop: 0,
      paddingBottom: 0,
    },
    textFieldRoot: {
      margin: '1rem',
      marginBottom: 0,
      padding: 0,
    },
    inputRoot: {
      width: '14rem',
      fontSize: '0.8125em',
      padding: '0.25em',
    },
    searchIcon: {
      marginLeft: '0.25em',
      marginRight: '0.25em',
    },
    inputAdornedStart: {
      paddingLeft: 0,
    },
    spinnerContainer: {
      padding: '1rem',
    },
    overlay: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      backdropFilter: 'blur(2px)',
    },
  })
);

function PopupSelectMenu<TId = string>({
  options,
  selectedId,
  editable,
  onSelect,
  onRemove,
  removeOption,
  renderOption,
  menuTitle,
  showSearch = true,
  menuItemClassName,
  renderTrigger,
}: PopupSelectMenuProps<TId>) {
  const classes = useStyles();

  const searchEl = React.useRef<HTMLInputElement | null>(null);
  const selectedItemRef = React.useRef<HTMLLIElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);
  const popoverId = open ? 'popup-select-menu' : undefined;

  useEffect(() => {
    if (open && showSearch) {
      searchEl.current?.focus();
    } else {
      setSearch('');
    }
  }, [open, showSearch]);

  useEffect(() => {
    if (!open || selectedId == null) return;

    const handle = window.setTimeout(() => {
      if (selectedItemRef.current) {
        selectedItemRef.current.scrollIntoView({ block: 'center' });
      }
    }, 0);

    return () => window.clearTimeout(handle);
  }, [open, selectedId]);

  const filteredOptions = useMemo(() => {
    const chunks = search.toLowerCase().split(/\s+/).filter(Boolean);

    return (options || []).filter(option => {
      const value = option.label.toLowerCase();
      return chunks.every(chunk => value.indexOf(chunk) >= 0);
    });
  }, [options, search]);

  const handleSearchUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  const openEditor = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    if (editable === false) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (id: TId) => {
    try {
      setLoading(true);
      await onSelect(id);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;
    try {
      setLoading(true);
      await onRemove();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <>
      {renderTrigger({
        open,
        selectedOption: options?.find(option => String(option.id) === (selectedId != null ? String(selectedId) : '')),
        onClick: openEditor,
      })}
      {open && (
        <Popover
          id={popoverId}
          open={open}
          anchorEl={anchorEl}
          onClick={event => event.stopPropagation()}
          onClose={handleClose}
          getContentAnchorEl={null}
          keepMounted
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {options ? (
            <Box className={classes.root}>
              <Box className={classes.content} style={loading ? { filter: 'blur(1px)' } : undefined}>
                {menuTitle && (
                  <Box px={2} pt={2} pb={showSearch ? 0 : 1}>
                    {menuTitle}
                  </Box>
                )}
                {showSearch && (
                  <Box className={classes.searchRow}>
                    <TextField
                      size="small"
                      variant="outlined"
                      value={search}
                      onChange={handleSearchUpdate}
                      inputRef={searchEl}
                      InputProps={{
                        startAdornment: <SearchIcon fontSize="small" className={classes.searchIcon} />,
                        autoCapitalize: 'off',
                        autoComplete: 'off',
                        autoCorrect: 'off',
                        classes: {
                          root: classes.inputRoot,
                          inputAdornedStart: classes.inputAdornedStart,
                        },
                      }}
                      classes={{
                        root: classes.textFieldRoot,
                      }}
                    />
                  </Box>
                )}
                <MenuList onClick={event => event.stopPropagation()} className={classes.menu}>
                  {filteredOptions.map(option => {
                    const isSelected = selectedId != null && String(option.id) === String(selectedId);
                    return (
                      <MenuItem
                        key={String(option.id)}
                        selected={isSelected}
                        className={menuItemClassName}
                        ref={isSelected ? selectedItemRef : undefined}
                        onClick={() => handleSelect(option.id)}
                      >
                        {renderOption ? renderOption({ option, isSelected }) : option.label}
                      </MenuItem>
                    );
                  })}
                  {!!onRemove && !!removeOption && <MenuItem onClick={handleRemove}>{removeOption}</MenuItem>}
                </MenuList>
              </Box>
              {loading && (
                <Box className={classes.overlay}>
                  <Spinner size="2rem" freezeAnimation={!open} />
                </Box>
              )}
            </Box>
          ) : (
            <div className={classes.spinnerContainer}>
              <Spinner freezeAnimation={!open} />
            </div>
          )}
        </Popover>
      )}
    </>
  );
}

export default PopupSelectMenu;
